/* eslint-disable max-len */
/**
 * @description Custom hooks for getting pilot easily.
 * If DataServiceContext cannot be gotten, use "getPilotValue" instead which needs to pass in
 * serverContext explicitly.
 *
 * Note: Define pilot name in "PILOTS"!
 */

import { useMemo, useContext } from 'react';
import { getPilotValue, PILOTS } from '@bingads-webui-campaign-react/smart-component-util';

import { DataServiceContext } from '../server-context';
import { useIsSmartPageStandalone, useIsUpIntegration } from '../integration-context';

export function usePilotsValue(key) {
  const serverContext = useContext(DataServiceContext);
  const isPilotEnabled = useMemo(
    () => (key ? getPilotValue(serverContext, key) : false),
    [key, serverContext]
  );

  return isPilotEnabled;
}

// SubAds phase one
export const useIsSubscriptionAdsEnabled = () => {
  const signup = usePilotsValue(PILOTS.isSubscriptionSignupEnabled);
  const postSignup = usePilotsValue(PILOTS.isUnifiedProductSubscriptionEnabled);

  return signup || postSignup;
};


export const useIsESCMsanPilotEnabled = () => usePilotsValue(PILOTS.isESCMsanEnabled);
export const useIsBusinessHourSectionEnabled = () => usePilotsValue(PILOTS.isSmartPageBusinessHourSectionEnabled);
export const useIsSmartPageImageTextSuggestionEnabled = () => usePilotsValue(PILOTS.isSmartPageImageTextSuggestionEnabled);
export const useIsSmartPageM365StockImageEnabled = () => usePilotsValue(PILOTS.isSmartPageM365StockImageEnabled);
export const useIsSmartPageM365StockImageGalleryEnabled = () => usePilotsValue(PILOTS.isSmartPageM365StockImageGalleryEnabled);
export const useIsSmartPageVideoSectionEnabled = () => usePilotsValue(PILOTS.isSmartPageVideoSectionEnabled);
export const useIsSmartPageMapEnabled = () => usePilotsValue(PILOTS.isSmartPageMapEnabled);
export const useIsSmartPageCustomizedUpdateEnabled = () => usePilotsValue(PILOTS.isSmartPageCustomizedUpdateEnabled);
export const useIsSmartPageM365BannerEnabled = () => usePilotsValue(PILOTS.isSmartPageM365BannerEnabled);
export const useIsSmartPageDarkModeEnabled = () => usePilotsValue(PILOTS.isSmartPageDarkModeEnabled);
export const useIsSmartPageProfessionalServicesEnabled = () => usePilotsValue(PILOTS.isSmartPageProfessionalServicesEnabled);
export const useIsSmartPageBookAppointmentEnabled = () => usePilotsValue(PILOTS.isSmartPageBookAppointmentEnabled);
export const useIsSmartPageEditorNavigationImprovementEnabled = () => usePilotsValue(PILOTS.isSmartPageEditorNavigationImprovementEnabled);
export const useIsSmartPageTemplateSuggestionEnabled = () => usePilotsValue(PILOTS.isSmartPageTemplateSuggestionEnabled);
export const useIsSmartPageImprovedReviewsEnabled = () => usePilotsValue(PILOTS.isSmartPageImprovedReviewsEnabled);
export const useIsSmartPageSectionReorderingEnabled = () => usePilotsValue(PILOTS.isSmartPageSectionReorderingEnabled);
export const useIsSmartPageEditorItemsReorderingEnabled = () => usePilotsValue(PILOTS.isSmartPageEditorItemsReorderingEnabled);
export const useIsSmartPagePreviewItemsReorderingEnabled = () => usePilotsValue(PILOTS.isSmartPagePreviewItemsReorderingEnabled);
export const useIsSmartPagePrivacyPolicyEnabled = () => usePilotsValue(PILOTS.isSmartPagePrivacyPolicyEnabled);
export const useIsSmartPageCustomDomainPreflightEnabled = () => usePilotsValue(PILOTS.isSmartPageCustomDomainPreflightEnabled);
export const useIsSmartPageCustomDomainPreflightPostSignupEnabled = () => usePilotsValue(PILOTS.isSmartPageCustomDomainPreflightPostSignupEnabled);
export const useIsSmartPageRuntimePageLocalizationEnabled = () => usePilotsValue(PILOTS.isSmartPageRuntimePageLocalizationEnabled);
export const useIsSmartPageRuntimePageWcpConsentEnabled = () => usePilotsValue(PILOTS.isSmartPageRuntimePageWcpConsentEnabled);
export const useIsSmartPageAttributesToWSDMigrationEnabled = () => usePilotsValue(PILOTS.isSmartPageAttributesToWSDMigrationEnabled);

// UI pilot, this pilot will never turn on in any environment, only for appending it in url for testing
const useIsSmartPageM365PremiumFeatureEnabled = () => usePilotsValue(PILOTS.isSmartPageM365PremiumFeatureEnabled);
// Account level pilot, not UI feature pilot, this value comes from an api call
const useIsM365PremiumUser = () => usePilotsValue(PILOTS.isM365PremiumUser);

// Only use this hook to control your UI feature, so it could control by the real MT flag
// or by manually appending queryString to enable the pilot in UI
export const useIsM365Premium = () => {
  const isM365PremiumUser = useIsM365PremiumUser();
  const isSmartPageM365PremiumFeatureEnabled = useIsSmartPageM365PremiumFeatureEnabled();

  return isM365PremiumUser || isSmartPageM365PremiumFeatureEnabled;
};

export const useIsSmartPageCustomDomainEnabled = () => {
  const isSmartPageStandalone = useIsSmartPageStandalone();
  const isUpIntegration = useIsUpIntegration();
  const isSmartPageCustomDomainEnabled = usePilotsValue(PILOTS.isSmartPageCustomDomainEnabled);
  const isSmartPageCustomDomainPreflightEnabled = useIsSmartPageCustomDomainPreflightEnabled();
  const isSmartPageCustomDomainPreflightPostSignupEnabled = useIsSmartPageCustomDomainPreflightPostSignupEnabled();

  // Since isSmartPageCustomDomainPreflightEnabled is defined as AB testing pilot, which only available in signup
  // so to make M365 available in post signup, we need to split the env
  return (isUpIntegration && isSmartPageCustomDomainPreflightEnabled && isSmartPageCustomDomainEnabled)
    || (isSmartPageStandalone && isSmartPageCustomDomainPreflightPostSignupEnabled && isSmartPageCustomDomainEnabled);
};

export const useAllPilots = () => {
  const serverContext = useContext(DataServiceContext);
  const isM365Premium = useIsM365Premium();

  const allPilots = useMemo(() => {
    const pilots = {};

    Object.keys(PILOTS).forEach((key) => {
      pilots[key] = key ? getPilotValue(serverContext, key) : false;
    });

    return pilots;
  }, [serverContext]);

  const finalPilots = useMemo(() => ({
    ...allPilots,
    isM365Premium,
  }), [allPilots, isM365Premium]);

  return finalPilots;
};
